import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function PrivacyPolicy() {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const updateCartCount = () => {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
        setCartCount(count);
      }
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <i className="ri-phone-line mr-2"></i>
                +1 (929) 268-8770
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                <i className="ri-medicine-bottle-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-blue-600">PillsForU</h1>
                <p className="text-xs text-gray-500">Trusted Healthcare</p>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link to="/cart" className="flex items-center text-gray-700 hover:text-blue-600 transition-colors cursor-pointer relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-2xl"></i>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-blue-100">Last Updated: January 2024</p>
        </div>
      </div>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              At PillsForU, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">1. Information We Collect</h2>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Personal Information</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We collect personal information that you voluntarily provide to us when you register on the website, place an order, subscribe to our newsletter, or contact us. This information may include:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>Full name and contact information (email address, phone number, mailing address)</li>
            <li>Payment information (credit card details, billing address)</li>
            <li>Medical information (prescription details, health conditions when relevant to your order)</li>
            <li>Account credentials (username, password)</li>
            <li>Order history and preferences</li>
          </ul>

          <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-6">Automatically Collected Information</h3>
          <p className="text-gray-700 mb-4 leading-relaxed">
            When you visit our website, we automatically collect certain information about your device and browsing behavior, including:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>IP address and browser type</li>
            <li>Operating system and device information</li>
            <li>Pages visited and time spent on our website</li>
            <li>Referring website addresses</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">2. How We Use Your Information</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Order Processing:</strong> To process and fulfill your orders, including payment processing, shipping, and delivery</li>
            <li><strong>Customer Service:</strong> To respond to your inquiries, provide customer support, and resolve any issues</li>
            <li><strong>Account Management:</strong> To create and manage your account, including authentication and security</li>
            <li><strong>Communication:</strong> To send you order confirmations, shipping updates, and important service announcements</li>
            <li><strong>Marketing:</strong> To send promotional emails about new products, special offers, and other information (with your consent)</li>
            <li><strong>Website Improvement:</strong> To analyze usage patterns and improve our website functionality and user experience</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
            <li><strong>Fraud Prevention:</strong> To detect, prevent, and address fraud, security issues, and technical problems</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">3. Information Sharing and Disclosure</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website, processing payments, and delivering orders (e.g., payment processors, shipping companies)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>Protection of Rights:</strong> To protect our rights, property, or safety, or that of our users or others</li>
            <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">4. Data Security</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>SSL encryption for all data transmission</li>
            <li>Secure payment processing through PCI-DSS compliant payment gateways</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Restricted access to personal information on a need-to-know basis</li>
            <li>Employee training on data protection and privacy practices</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">5. Cookies and Tracking Technologies</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content. Cookies are small text files stored on your device that help us remember your preferences and improve our services.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed">
            You can control cookie settings through your browser preferences. However, disabling cookies may limit your ability to use certain features of our website.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">6. Your Privacy Rights</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Depending on your location, you may have the following rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
            <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
            <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Restriction:</strong> Request restriction of processing of your personal information</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed">
            To exercise any of these rights, please contact us at sam4104t@gmail.com or call +1 (929) 268-8770.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">7. Children's Privacy</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Our website and services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information promptly.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">8. International Data Transfers</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to the transfer of your information to these countries. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">9. Third-Party Links</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">10. Changes to This Privacy Policy</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes your acceptance of the updated policy.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-8">11. Contact Us</h2>
          <p className="text-gray-700 mb-4 leading-relaxed">
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <p className="text-gray-700 mb-2"><strong>Email:</strong> sam4104t@gmail.com</p>
            <p className="text-gray-700 mb-2"><strong>Phone:</strong> +1 (929) 268-8770</p>
            <p className="text-gray-700"><strong>Website:</strong> pillsforu.com</p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mt-8">
            <p className="text-gray-700 leading-relaxed">
              <strong>Your privacy is important to us.</strong> We are committed to maintaining the confidentiality and security of your personal information. Thank you for trusting PillsForU with your healthcare needs.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="hover:text-blue-400 transition-colors cursor-pointer">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund-policy" className="hover:text-blue-400 transition-colors cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p>Phone: +1 (929) 268-8770</p>
              <p>Email: sam4104t@gmail.com</p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 PillsForU.com. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}